import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Star,
  Trophy,
  BookOpen,
  Lightbulb,
  ShoppingBag,
  DollarSign,
  Shield,
  Coins,
} from "lucide-react";
import { getCurrentUser } from "../utils/auth";
import Layout from "../components/Layout";
import { Story, StoryChoice, StoryOutcome } from "../types";

const StoryPage: React.FC = () => {
  useParams(); // Para manter a funcionalidade de roteamento
  const [user] = useState(getCurrentUser());
  const navigate = useNavigate();

  const [currentScenario, setCurrentScenario] = useState(0);
  const [storyCompleted, setStoryCompleted] = useState(false);
  const [showOutcome, setShowOutcome] = useState(false);
  const [starsEarned, setStarsEarned] = useState(0);
  const [currentOutcome, setCurrentOutcome] = useState<StoryOutcome | null>(
    null
  );

  // Dados da história (simulados)
  const [story] = useState<Story>({
    id: "financial-journey",
    title: "Ana's Financial Journey",
    scenario:
      "Ana is a young professional who just received her first salary. She has $3,000 per month and needs to make important decisions about how to manage her money.",
    choices: [
      {
        id: "choice-1",
        text: "Spend everything on clothes and entertainment",
        consequence: "Ana spends all her money on non-essential items",
        nextScenario: "scenario-2",
      },
      {
        id: "choice-2",
        text: "Make a budget and save 20%",
        consequence: "Ana decides to be responsible with her money",
        nextScenario: "scenario-3",
      },
      {
        id: "choice-3",
        text: "Invest everything in high-risk stocks",
        consequence: "Ana decides to invest aggressively",
        nextScenario: "scenario-4",
      },
    ],
    outcomes: [
      {
        id: "outcome-1",
        scenario: "scenario-2",
        result:
          "Ana ran out of money at the end of the month and had to take a loan",
        lesson:
          "Spending without planning can lead to serious financial problems",
        stars: 1,
      },
      {
        id: "outcome-2",
        scenario: "scenario-3",
        result: "Ana managed to save $600 and still had money for emergencies",
        lesson:
          "Making a budget and saving regularly is the foundation of financial health",
        stars: 3,
      },
      {
        id: "outcome-3",
        scenario: "scenario-4",
        result: "Ana lost 30% of her money due to market volatility",
        lesson:
          "High-risk investments should only be made with money you can afford to lose",
        stars: 2,
      },
    ],
  });

  const scenarios = [
    {
      id: "scenario-1",
      title: "O Primeiro Salário",
      content:
        "Ana acabou de receber seu primeiro salário de R$ 3.000. Ela está muito animada e não sabe exatamente o que fazer com esse dinheiro. Suas amigas sugerem diferentes opções...",
      image: DollarSign,
      choices: story.choices,
    },
    {
      id: "scenario-2",
      title: "As Consequências",
      content:
        "Após gastar todo seu dinheiro, Ana percebe que não tem mais nada para pagar as contas do próximo mês. Ela está preocupada e não sabe como resolver a situação.",
      image: "😰",
      choices: [
        {
          id: "choice-4",
          text: "Pedir dinheiro emprestado para os pais",
          consequence: "Ana pede ajuda familiar",
          nextScenario: "end",
        },
        {
          id: "choice-5",
          text: "Procurar um segundo emprego",
          consequence: "Ana decide trabalhar mais para resolver a situação",
          nextScenario: "end",
        },
      ],
    },
    {
      id: "scenario-3",
      title: "A Decisão Inteligente",
      content:
        "Ana decidiu ser responsável e criou um orçamento. Ela separou R$ 600 para poupança, R$ 1.500 para despesas essenciais e R$ 900 para lazer. Como ela se sente?",
      image: "😊",
      choices: [
        {
          id: "choice-6",
          text: "Continuar com o planejamento financeiro",
          consequence: "Ana mantém sua disciplina financeira",
          nextScenario: "end",
        },
        {
          id: "choice-7",
          text: "Investir parte da poupança em educação",
          consequence: "Ana decide investir em si mesma",
          nextScenario: "end",
        },
      ],
    },
    {
      id: "scenario-4",
      title: "A Lição Aprendida",
      content:
        "Ana perdeu dinheiro com investimentos de alto risco. Ela aprendeu que investir sem conhecimento pode ser perigoso. O que ela deve fazer agora?",
      image: BookOpen,
      choices: [
        {
          id: "choice-8",
          text: "Estudar mais sobre investimentos antes de investir",
          consequence: "Ana decide se educar financeiramente",
          nextScenario: "end",
        },
        {
          id: "choice-9",
          text: "Voltar para investimentos mais seguros",
          consequence: "Ana opta por uma abordagem mais conservadora",
          nextScenario: "end",
        },
      ],
    },
  ];

  useEffect(() => {
    if (!user) {
      navigate("/login");
      return;
    }
  }, [user, navigate]);

  const handleChoiceSelect = (choice: StoryChoice) => {
    // Encontrar o próximo cenário
    const nextScenarioIndex = scenarios.findIndex(
      (s) => s.id === choice.nextScenario
    );

    if (nextScenarioIndex !== -1) {
      setCurrentScenario(nextScenarioIndex);
    } else {
      // Fim da história - encontrar o resultado
      const outcome = story.outcomes.find(
        (o) => o.scenario === choice.nextScenario
      );
      if (outcome) {
        setCurrentOutcome(outcome);
        setStarsEarned(outcome.stars);
        setShowOutcome(true);
        setStoryCompleted(true);
      }
    }
  };

  const getCurrentScenarioData = () => {
    return scenarios[currentScenario];
  };

  const getChoiceIcon = (choiceId: string) => {
    if (choiceId.includes("choice-1") || choiceId.includes("choice-4"))
      return ShoppingBag;
    if (choiceId.includes("choice-2") || choiceId.includes("choice-6"))
      return DollarSign;
    if (choiceId.includes("choice-3") || choiceId.includes("choice-8"))
      return Trophy;
    if (choiceId.includes("choice-5") || choiceId.includes("choice-7"))
      return Trophy;
    if (choiceId.includes("choice-9")) return Shield;
    return Lightbulb;
  };

  const getChoiceColor = (choiceId: string) => {
    if (choiceId.includes("choice-1") || choiceId.includes("choice-4"))
      return "from-red-500 to-red-600";
    if (choiceId.includes("choice-2") || choiceId.includes("choice-6"))
      return "from-green-500 to-green-600";
    if (choiceId.includes("choice-3") || choiceId.includes("choice-8"))
      return "from-blue-500 to-blue-600";
    if (choiceId.includes("choice-5") || choiceId.includes("choice-7"))
      return "from-purple-500 to-purple-600";
    if (choiceId.includes("choice-9")) return "from-yellow-500 to-yellow-600";
    return "from-gray-500 to-gray-600";
  };

  if (!user) {
    return null;
  }

  if (storyCompleted && showOutcome && currentOutcome) {
    return (
      <Layout
        title="História Concluída!"
        subtitle="Você completou a jornada de Ana"
      >
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.5 }}
          className="max-w-2xl mx-auto"
        >
          <div className="card text-center mb-6">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="text-6xl mb-4"
            >
              <Trophy className="w-16 h-16 text-yellow-400 mx-auto" />
            </motion.div>

            <h2 className="text-2xl font-bold text-white mb-2">Parabéns!</h2>

            <p className="text-gray-400 mb-6">
              Você completou a história interativa
            </p>

            {/* Resultado da história */}
            <div className="bg-gradient-to-r from-primary-600/20 to-blue-600/20 border border-primary-500/30 rounded-lg p-6 mb-6">
              <h3 className="text-lg font-semibold text-white mb-3">
                Resultado da História
              </h3>
              <p className="text-gray-300 mb-4">{currentOutcome.result}</p>

              <div className="bg-blue-600/20 border border-blue-500/30 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <Lightbulb className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <h4 className="text-blue-400 font-semibold mb-2">
                      Lição Aprendida:
                    </h4>
                    <p className="text-gray-300 text-sm leading-relaxed">
                      {currentOutcome.lesson}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Estrelas conquistadas */}
            <div className="flex justify-center space-x-2 mb-6">
              {[1, 2, 3].map((star) => (
                <motion.div
                  key={star}
                  initial={{ scale: 0, rotate: -180 }}
                  animate={{
                    scale: star <= starsEarned ? 1 : 0.5,
                    rotate: 0,
                  }}
                  transition={{
                    duration: 0.3,
                    delay: 0.5 + star * 0.1,
                  }}
                  className={`text-3xl ${
                    star <= starsEarned ? "text-yellow-400" : "text-gray-600"
                  }`}
                >
                  <Star className="w-8 h-8 text-yellow-400" />
                </motion.div>
              ))}
            </div>

            {/* Recompensas */}
            <div className="bg-gradient-to-r from-primary-600/20 to-blue-600/20 border border-primary-500/30 rounded-lg p-4 mb-6">
              <h3 className="text-lg font-semibold text-white mb-3">
                Recompensas Conquistadas
              </h3>
              <div className="flex justify-center space-x-6">
                <div className="flex items-center space-x-2">
                  <Star className="w-5 h-5 text-yellow-400" />
                  <span className="text-white font-semibold">
                    +{starsEarned * 10}
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <Coins className="w-6 h-6 text-green-400" />
                  <span className="text-white font-semibold">
                    +{starsEarned * 5}
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <Trophy className="w-5 h-5 text-yellow-400" />
                  <span className="text-white font-semibold">
                    Contador de Histórias!
                  </span>
                </div>
              </div>
            </div>

            {/* Botões de ação */}
            <div className="flex space-x-3">
              <button
                onClick={() => navigate("/finance-school")}
                className="flex-1 py-3 bg-dark-700 hover:bg-dark-600 text-white font-medium rounded-lg transition-colors"
              >
                Voltar à Escolinha
              </button>
              <button
                onClick={() => {
                  setCurrentScenario(0);
                  setStoryCompleted(false);
                  setShowOutcome(false);
                  setStarsEarned(0);
                  setCurrentOutcome(null);
                }}
                className="flex-1 py-3 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg transition-colors"
              >
                Jogar Novamente
              </button>
            </div>
          </div>
        </motion.div>
      </Layout>
    );
  }

  const currentScenarioData = getCurrentScenarioData();

  return (
    <Layout
      title={story.title}
      subtitle={`Cenário ${currentScenario + 1} de ${scenarios.length}`}
    >
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5 }}
        className="max-w-2xl mx-auto"
      >
        {/* Header da história */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => navigate("/finance-school")}
            className="flex items-center space-x-2 text-gray-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Voltar</span>
          </button>

          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <BookOpen className="w-4 h-4 text-primary-400" />
              <span className="text-white font-semibold">
                {currentScenario + 1}/{scenarios.length}
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <Star className="w-4 h-4 text-yellow-400" />
              <span className="text-white font-semibold">{starsEarned}</span>
            </div>
          </div>
        </div>

        {/* Barra de progresso */}
        <div className="mb-6">
          <div className="flex justify-between text-sm text-gray-400 mb-2">
            <span>Progresso da História</span>
            <span>
              {Math.round(((currentScenario + 1) / scenarios.length) * 100)}%
            </span>
          </div>
          <div className="w-full bg-dark-700 rounded-full h-2">
            <motion.div
              initial={{ width: 0 }}
              animate={{
                width: `${((currentScenario + 1) / scenarios.length) * 100}%`,
              }}
              transition={{ duration: 0.3 }}
              className="h-2 bg-gradient-to-r from-primary-500 to-blue-500 rounded-full"
            />
          </div>
        </div>

        {/* Cenário atual */}
        <motion.div
          key={currentScenario}
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -50 }}
          transition={{ duration: 0.3 }}
          className="card mb-6"
        >
          <div className="text-center mb-6">
            <div className="text-6xl mb-4">
              {React.createElement(currentScenarioData.image, {
                className: "w-16 h-16 text-primary-400 mx-auto",
              })}
            </div>
            <h2 className="text-2xl font-semibold text-white mb-4">
              {currentScenarioData.title}
            </h2>
            <p className="text-gray-300 text-lg leading-relaxed">
              {currentScenarioData.content}
            </p>
          </div>

          {/* Escolhas */}
          <div className="space-y-3">
            <h3 className="text-lg font-semibold text-white mb-4 text-center">
              O que Ana deve fazer?
            </h3>

            {currentScenarioData.choices.map((choice, index) => (
              <motion.button
                key={choice.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
                onClick={() => handleChoiceSelect(choice)}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className={`w-full p-4 rounded-lg transition-all duration-200 bg-gradient-to-r ${getChoiceColor(
                  choice.id
                )} hover:shadow-lg`}
              >
                <div className="flex items-center space-x-3">
                  {React.createElement(getChoiceIcon(choice.id), {
                    className: "w-6 h-6 text-white",
                  })}
                  <span className="text-white font-medium text-left flex-1">
                    {choice.text}
                  </span>
                </div>
              </motion.button>
            ))}
          </div>
        </motion.div>

        {/* Dicas da história */}
        <div className="bg-gradient-to-r from-yellow-600/20 to-orange-600/20 border border-yellow-500/30 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <Lightbulb className="w-5 h-5 text-yellow-400 mt-0.5 flex-shrink-0" />
            <div>
              <h4 className="text-yellow-400 font-semibold mb-2 flex items-center space-x-2">
                <Lightbulb className="w-5 h-5" />
                <span>Dica:</span>
              </h4>
              <p className="text-gray-300 text-sm leading-relaxed">
                Pense nas consequências de cada escolha. Na vida real, decisões
                financeiras têm impacto a longo prazo. Considere sempre o
                equilíbrio entre prazer imediato e segurança futura.
              </p>
            </div>
          </div>
        </div>
      </motion.div>
    </Layout>
  );
};

export default StoryPage;
